/**
 * `TimeLockedBalance` locks a `Balance<T>` such that only `unlock_per_second` of the amount
 * gets unlocked (and becomes withdrawable) every second starting from `unlock_start_ts_sec`.
 * It allows for `unlock_per_second` and `unlock_start_ts_sec` to be safely changed and allows for
 * aditional
 * balance to be added at any point via the `top_up` function.
 *
 * This module doesn't implement any permission functionality and it is intended to be used
 * as a basic building block and to provide safety guarantees for building more complex token
 * emission modules (e.g. vesting).
 */

import { bcs } from '@mysten/sui/bcs'
import type { ClientWithCoreApi, SuiClientTypes } from '@mysten/sui/client'
import type { SuiObjectData, SuiParsedData } from '@mysten/sui/jsonRpc'
import { fromBase64 } from '@mysten/sui/utils'
import { getTypeOrigin } from '../../_envs'
import {
  assertFieldsWithTypesArgsMatch,
  assertReifiedTypeArgsMatch,
  decodeFromFields,
  decodeFromFieldsWithTypes,
  decodeFromJSONField,
  extractType,
  phantom,
  PhantomReified,
  PhantomToTypeStr,
  PhantomTypeArgument,
  Reified,
  StructClass,
  ToField,
  ToJSON,
  ToPhantomTypeArgument,
  ToTypeStr,
} from '../../_framework/reified'
import {
  composeSuiType,
  compressSuiType,
  FieldsWithTypes,
  parseTypeName,
} from '../../_framework/util'
import { Balance } from '../../sui/balance/structs'

/* ============================== TimeLockedBalance =============================== */

export function isTimeLockedBalance(type: string): boolean {
  type = compressSuiType(type)
  return type.startsWith(
    `${
      getTypeOrigin('kai-sav', 'time_locked_balance::TimeLockedBalance')
    }::time_locked_balance::TimeLockedBalance` + '<',
  )
}

export interface TimeLockedBalanceFields<T extends PhantomTypeArgument> {
  lockedBalance: ToField<Balance<T>>
  unlockStartTsSec: ToField<'u64'>
  unlockPerSecond: ToField<'u64'>
  /** Balance that gets unlocked and is withdrawable is stored here. */
  unlockedBalance: ToField<Balance<T>>
  /** Time at which all of the balance will become unlocked. Unix timestamp. */
  finalUnlockTsSec: ToField<'u64'>
  previousUnlockAt: ToField<'u64'>
}

export type TimeLockedBalanceReified<T extends PhantomTypeArgument> = Reified<
  TimeLockedBalance<T>,
  TimeLockedBalanceFields<T>
>

export type TimeLockedBalanceJSONField<T extends PhantomTypeArgument> = {
  lockedBalance: ToJSON<Balance<T>>
  unlockStartTsSec: string
  unlockPerSecond: string
  unlockedBalance: ToJSON<Balance<T>>
  finalUnlockTsSec: string
  previousUnlockAt: string
}

export type TimeLockedBalanceJSON<T extends PhantomTypeArgument> = {
  $typeName: typeof TimeLockedBalance.$typeName
  $typeArgs: [PhantomToTypeStr<T>]
} & TimeLockedBalanceJSONField<T>

/**
 * Wraps a `Balance<T>` and allows only `unlock_per_second` of it to be withdrawn
 * per second starting from `unlock_start_ts_sec`. All timestamp fields are unix timestamp.
 */
export class TimeLockedBalance<T extends PhantomTypeArgument> implements StructClass {
  __StructClass = true as const

  static get $typeName(): `${string}::time_locked_balance::TimeLockedBalance` {
    return `${
      getTypeOrigin('kai-sav', 'time_locked_balance::TimeLockedBalance')
    }::time_locked_balance::TimeLockedBalance` as const
  }
  static readonly $numTypeParams = 1
  static readonly $isPhantom = [true] as const

  readonly $typeName: typeof TimeLockedBalance.$typeName = TimeLockedBalance.$typeName
  readonly $fullTypeName: `${string}::time_locked_balance::TimeLockedBalance<${PhantomToTypeStr<
    T
  >}>`
  readonly $typeArgs: [PhantomToTypeStr<T>]
  readonly $isPhantom: typeof TimeLockedBalance.$isPhantom = TimeLockedBalance.$isPhantom

  readonly lockedBalance: ToField<Balance<T>>
  readonly unlockStartTsSec: ToField<'u64'>
  readonly unlockPerSecond: ToField<'u64'>
  /** Balance that gets unlocked and is withdrawable is stored here. */
  readonly unlockedBalance: ToField<Balance<T>>
  /** Time at which all of the balance will become unlocked. Unix timestamp. */
  readonly finalUnlockTsSec: ToField<'u64'>
  readonly previousUnlockAt: ToField<'u64'>

  private constructor(typeArgs: [PhantomToTypeStr<T>], fields: TimeLockedBalanceFields<T>) {
    this.$fullTypeName = composeSuiType(
      TimeLockedBalance.$typeName,
      ...typeArgs,
    ) as `${string}::time_locked_balance::TimeLockedBalance<${PhantomToTypeStr<T>}>`
    this.$typeArgs = typeArgs

    this.lockedBalance = fields.lockedBalance
    this.unlockStartTsSec = fields.unlockStartTsSec
    this.unlockPerSecond = fields.unlockPerSecond
    this.unlockedBalance = fields.unlockedBalance
    this.finalUnlockTsSec = fields.finalUnlockTsSec
    this.previousUnlockAt = fields.previousUnlockAt
  }

  static reified<T extends PhantomReified<PhantomTypeArgument>>(
    T: T,
  ): TimeLockedBalanceReified<ToPhantomTypeArgument<T>> {
    const reifiedBcs = TimeLockedBalance.bcs
    return {
      get typeName() {
        return TimeLockedBalance.$typeName
      },
      get fullTypeName() {
        return composeSuiType(
          TimeLockedBalance.$typeName,
          ...[extractType(T)],
        ) as `${string}::time_locked_balance::TimeLockedBalance<${PhantomToTypeStr<
          ToPhantomTypeArgument<T>
        >}>`
      },
      get typeArgs() {
        return [extractType(T)] as [PhantomToTypeStr<ToPhantomTypeArgument<T>>]
      },
      isPhantom: TimeLockedBalance.$isPhantom,
      reifiedTypeArgs: [T],
      fromFields: (fields: Record<string, any>) => TimeLockedBalance.fromFields(T, fields),
      fromFieldsWithTypes: (item: FieldsWithTypes) =>
        TimeLockedBalance.fromFieldsWithTypes(T, item),
      fromBcs: (data: Uint8Array) => TimeLockedBalance.fromFields(T, reifiedBcs.parse(data)),
      bcs: reifiedBcs,
      fromJSONField: (field: any) => TimeLockedBalance.fromJSONField(T, field),
      fromJSON: (json: Record<string, any>) => TimeLockedBalance.fromJSON(T, json),
      fromCoreObject: (obj: SuiClientTypes.Object<{ content: true }>) =>
        TimeLockedBalance.fromCoreObject(T, obj),
      fromSuiParsedData: (content: SuiParsedData) =>
        TimeLockedBalance.fromSuiParsedData(T, content),
      fromSuiObjectData: (content: SuiObjectData) =>
        TimeLockedBalance.fromSuiObjectData(T, content),
      fetch: async (client: ClientWithCoreApi, id: string) =>
        TimeLockedBalance.fetch(client, T, id),
      new: (fields: TimeLockedBalanceFields<ToPhantomTypeArgument<T>>) => {
        return new TimeLockedBalance([extractType(T)], fields)
      },
      kind: 'StructClassReified',
    }
  }

  static get r(): typeof TimeLockedBalance.reified {
    return TimeLockedBalance.reified
  }

  static phantom<T extends PhantomReified<PhantomTypeArgument>>(
    T: T,
  ): PhantomReified<ToTypeStr<TimeLockedBalance<ToPhantomTypeArgument<T>>>> {
    return phantom(TimeLockedBalance.reified(T))
  }

  static get p(): typeof TimeLockedBalance.phantom {
    return TimeLockedBalance.phantom
  }

  private static instantiateBcs() {
    return bcs.struct('TimeLockedBalance', {
      locked_balance: Balance.bcs,
      unlock_start_ts_sec: bcs.u64(),
      unlock_per_second: bcs.u64(),
      unlocked_balance: Balance.bcs,
      final_unlock_ts_sec: bcs.u64(),
      previous_unlock_at: bcs.u64(),
    })
  }

  private static cachedBcs: ReturnType<typeof TimeLockedBalance.instantiateBcs> | null = null

  static get bcs(): ReturnType<typeof TimeLockedBalance.instantiateBcs> {
    if (!TimeLockedBalance.cachedBcs) {
      TimeLockedBalance.cachedBcs = TimeLockedBalance.instantiateBcs()
    }
    return TimeLockedBalance.cachedBcs
  }

  static fromFields<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    fields: Record<string, any>,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    return TimeLockedBalance.reified(typeArg).new({
      lockedBalance: decodeFromFields(Balance.reified(typeArg), fields.locked_balance),
      unlockStartTsSec: decodeFromFields('u64', fields.unlock_start_ts_sec),
      unlockPerSecond: decodeFromFields('u64', fields.unlock_per_second),
      unlockedBalance: decodeFromFields(Balance.reified(typeArg), fields.unlocked_balance),
      finalUnlockTsSec: decodeFromFields('u64', fields.final_unlock_ts_sec),
      previousUnlockAt: decodeFromFields('u64', fields.previous_unlock_at),
    })
  }

  static fromFieldsWithTypes<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    item: FieldsWithTypes,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    if (!isTimeLockedBalance(item.type)) {
      throw new Error('not a TimeLockedBalance type')
    }
    assertFieldsWithTypesArgsMatch(item, [typeArg])

    return TimeLockedBalance.reified(typeArg).new({
      lockedBalance: decodeFromFieldsWithTypes(
        Balance.reified(typeArg),
        item.fields.locked_balance,
      ),
      unlockStartTsSec: decodeFromFieldsWithTypes('u64', item.fields.unlock_start_ts_sec),
      unlockPerSecond: decodeFromFieldsWithTypes('u64', item.fields.unlock_per_second),
      unlockedBalance: decodeFromFieldsWithTypes(
        Balance.reified(typeArg),
        item.fields.unlocked_balance,
      ),
      finalUnlockTsSec: decodeFromFieldsWithTypes('u64', item.fields.final_unlock_ts_sec),
      previousUnlockAt: decodeFromFieldsWithTypes('u64', item.fields.previous_unlock_at),
    })
  }

  static fromBcs<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    data: Uint8Array,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    return TimeLockedBalance.fromFields(typeArg, TimeLockedBalance.bcs.parse(data))
  }

  toJSONField(): TimeLockedBalanceJSONField<T> {
    return {
      lockedBalance: this.lockedBalance.toJSONField(),
      unlockStartTsSec: this.unlockStartTsSec.toString(),
      unlockPerSecond: this.unlockPerSecond.toString(),
      unlockedBalance: this.unlockedBalance.toJSONField(),
      finalUnlockTsSec: this.finalUnlockTsSec.toString(),
      previousUnlockAt: this.previousUnlockAt.toString(),
    }
  }

  toJSON(): TimeLockedBalanceJSON<T> {
    return { $typeName: this.$typeName, $typeArgs: this.$typeArgs, ...this.toJSONField() }
  }

  static fromJSONField<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    field: any,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    return TimeLockedBalance.reified(typeArg).new({
      lockedBalance: decodeFromJSONField(Balance.reified(typeArg), field.lockedBalance),
      unlockStartTsSec: decodeFromJSONField('u64', field.unlockStartTsSec),
      unlockPerSecond: decodeFromJSONField('u64', field.unlockPerSecond),
      unlockedBalance: decodeFromJSONField(Balance.reified(typeArg), field.unlockedBalance),
      finalUnlockTsSec: decodeFromJSONField('u64', field.finalUnlockTsSec),
      previousUnlockAt: decodeFromJSONField('u64', field.previousUnlockAt),
    })
  }

  static fromJSON<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    json: Record<string, any>,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    if (json.$typeName !== TimeLockedBalance.$typeName) {
      throw new Error(
        `not a TimeLockedBalance json object: expected '${TimeLockedBalance.$typeName}' but got '${json.$typeName}'`,
      )
    }
    assertReifiedTypeArgsMatch(
      composeSuiType(TimeLockedBalance.$typeName, ...[extractType(typeArg)]),
      json.$typeArgs,
      [typeArg],
    )

    return TimeLockedBalance.fromJSONField(typeArg, json)
  }

  static fromCoreObject<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    obj: SuiClientTypes.Object<{ content: true }>,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    if (!isTimeLockedBalance(obj.type)) {
      throw new Error(`object at ${obj.objectId} is not a TimeLockedBalance object`)
    }

    const gotTypeArgs = parseTypeName(obj.type).typeArgs
    if (gotTypeArgs.length !== 1) {
      throw new Error(
        `type argument mismatch: expected 1 type arguments but got '${gotTypeArgs.length}'`,
      )
    }
    for (let i = 0; i < 1; i++) {
      const gotTypeArg = compressSuiType(gotTypeArgs[i])
      const expectedTypeArg = compressSuiType(extractType([typeArg][i]))
      if (gotTypeArg !== expectedTypeArg) {
        throw new Error(
          `type argument mismatch at position ${i}: expected '${expectedTypeArg}' but got '${gotTypeArg}'`,
        )
      }
    }

    return TimeLockedBalance.fromBcs(typeArg, obj.content)
  }

  /** @deprecated `SuiParsedData` is a JSON-RPC-only type that is being phased out upstream. Use {@link TimeLockedBalance.fromCoreObject} together with `client.core.getObject({ include: { content: true } })` for transport-agnostic parsing. */
  static fromSuiParsedData<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    content: SuiParsedData,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    if (content.dataType !== 'moveObject') {
      throw new Error('not an object')
    }
    if (!isTimeLockedBalance(content.type)) {
      throw new Error(`object at ${(content.fields as any).id} is not a TimeLockedBalance object`)
    }
    return TimeLockedBalance.fromFieldsWithTypes(typeArg, content)
  }

  /** @deprecated `SuiObjectData` is a JSON-RPC-only type that is being phased out upstream. Use {@link TimeLockedBalance.fromCoreObject} together with `client.core.getObject({ include: { content: true } })` for transport-agnostic parsing. */
  static fromSuiObjectData<T extends PhantomReified<PhantomTypeArgument>>(
    typeArg: T,
    data: SuiObjectData,
  ): TimeLockedBalance<ToPhantomTypeArgument<T>> {
    if (data.bcs) {
      if (data.bcs.dataType !== 'moveObject' || !isTimeLockedBalance(data.bcs.type)) {
        throw new Error(`object at is not a TimeLockedBalance object`)
      }

      const gotTypeArgs = parseTypeName(data.bcs.type).typeArgs
      if (gotTypeArgs.length !== 1) {
        throw new Error(
          `type argument mismatch: expected 1 type arguments but got '${gotTypeArgs.length}'`,
        )
      }
      for (let i = 0; i < 1; i++) {
        const gotTypeArg = compressSuiType(gotTypeArgs[i])
        const expectedTypeArg = compressSuiType(extractType([typeArg][i]))
        if (gotTypeArg !== expectedTypeArg) {
          throw new Error(
            `type argument mismatch at position ${i}: expected '${expectedTypeArg}' but got '${gotTypeArg}'`,
          )
        }
      }

      return TimeLockedBalance.fromBcs(typeArg, fromBase64(data.bcs.bcsBytes))
    }
    if (data.content) {
      return TimeLockedBalance.fromSuiParsedData(typeArg, data.content)
    }
    throw new Error(
      'Both `bcs` and `content` fields are missing from the data. Include `showBcs` or `showContent` in the request.',
    )
  }

  static async fetch<T extends PhantomReified<PhantomTypeArgument>>(
    client: ClientWithCoreApi,
    typeArg: T,
    id: string,
  ): Promise<TimeLockedBalance<ToPhantomTypeArgument<T>>> {
    const { object } = await client.core.getObject({
      objectId: id,
      include: { content: true },
    })
    if (!isTimeLockedBalance(object.type)) {
      throw new Error(`object at id ${id} is not a TimeLockedBalance object`)
    }

    const gotTypeArgs = parseTypeName(object.type).typeArgs
    if (gotTypeArgs.length !== 1) {
      throw new Error(
        `type argument mismatch: expected 1 type arguments but got '${gotTypeArgs.length}'`,
      )
    }
    for (let i = 0; i < 1; i++) {
      const gotTypeArg = compressSuiType(gotTypeArgs[i])
      const expectedTypeArg = compressSuiType(extractType([typeArg][i]))
      if (gotTypeArg !== expectedTypeArg) {
        throw new Error(
          `type argument mismatch at position ${i}: expected '${expectedTypeArg}' but got '${gotTypeArg}'`,
        )
      }
    }

    return TimeLockedBalance.fromBcs(typeArg, object.content)
  }
}
